import React, { useCallback, useEffect, useRef } from 'react';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Underline } from 'lucide-react';
import { usePrompt } from '../contexts/ConfirmContext';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  className?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Escreva aqui...',
  minHeight = '140px',
  className = '',
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const prompt = usePrompt();

  useEffect(() => {
    const el = editorRef.current;
    if (!el || el.innerHTML === (value || '')) return;
    el.innerHTML = value || '';
  }, [value]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML === '<br>' ? '' : el.innerHTML;
    onChange(html);
  }, [onChange]);

  const exec = (command: string, commandValue?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const addLink = async () => {
    // O diálogo rouba o foco do editor: guarda a seleção para aplicar o link no trecho certo
    const selection = window.getSelection();
    const range =
      selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)
        ? selection.getRangeAt(0).cloneRange()
        : null;
    const url = await prompt({
      title: 'Inserir link',
      label: 'URL do link',
      defaultValue: 'https://',
      required: true,
      confirmText: 'Inserir'
    });
    if (!url?.trim()) return;
    editorRef.current?.focus();
    if (range) {
      const current = window.getSelection();
      current?.removeAllRanges();
      current?.addRange(range);
    }
    exec('createLink', url.trim());
  };

  const toolbarBtn =
    'p-2 rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors disabled:opacity-40';

  return (
    <div
      className={`border border-gray-300 rounded-md overflow-hidden bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent ${className}`}
    >
      <div
        className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-gray-200 bg-gray-50"
        role="toolbar"
        aria-label="Formatação de texto"
      >
        <button type="button" className={toolbarBtn} title="Negrito" onClick={() => exec('bold')}>
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" className={toolbarBtn} title="Itálico" onClick={() => exec('italic')}>
          <Italic className="w-4 h-4" />
        </button>
        <button type="button" className={toolbarBtn} title="Sublinhado" onClick={() => exec('underline')}>
          <Underline className="w-4 h-4" />
        </button>
        <span className="w-px h-6 bg-gray-300 mx-1" aria-hidden />
        <button
          type="button"
          className={toolbarBtn}
          title="Lista com marcadores"
          onClick={() => exec('insertUnorderedList')}
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          className={toolbarBtn}
          title="Lista numerada"
          onClick={() => exec('insertOrderedList')}
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <span className="w-px h-6 bg-gray-300 mx-1" aria-hidden />
        <button type="button" className={toolbarBtn} title="Inserir link" onClick={addLink}>
          <LinkIcon className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        className="rich-text-editor px-3 py-2 text-sm text-gray-900 focus:outline-none"
        style={{ minHeight }}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
